import React, { useEffect, useRef, useState } from 'react';
import {
  ArrowRight,
  BarChart3,
  Bot,
  BrainCircuit,
  Briefcase,
  BriefcaseBusiness,
  Calendar,
  Check,
  ChevronDown,
  ClipboardCheck,
  FileCheck2,
  FileText,
  Gauge,
  Lightbulb,
  LineChart,
  LockKeyhole,
  LayoutDashboard,
  MessageSquareText,
  Scale,
  SearchCheck,
  ShieldCheck,
  Sparkles,
  UserCheck,
  UsersRound,
} from 'lucide-react';
import Header from '../Header&Footer/Header';
import Footer from '../Header&Footer/Footer';
import ScrollReveal from '../HomePage/ScrollReveal';
import useSEO from '../HomePage/useSEO';
import './AIHiringGuide.css';

const adoptionStats = [
  { value: '72%', label: 'HR professionals using AI weekly (2025)', icon: BrainCircuit, tone: 'green' },
  { value: '63%', label: 'HR professionals reporting greater productivity', icon: BarChart3, tone: 'mint' },
  { value: '53%', label: 'HR leaders trusting AI hiring recommendations (2025)', icon: ShieldCheck, tone: 'blue' },
  { value: '79%', label: 'Candidates wanting transparency about AI\'s role', icon: Lightbulb, tone: 'blue' },
];

const impactItems = [
  { label: 'Resume screening', value: 35, icon: FileText },
  { label: 'Training', value: 35, icon: ClipboardCheck },
  { label: 'Candidate communications', value: 34, icon: MessageSquareText },
  { label: 'Recruiting', value: 33, icon: UsersRound },
  { label: 'Candidate screening', value: 31, icon: SearchCheck },
  { label: 'Assessments', value: 31, icon: Gauge },
];

const candidateUseItems = [
  { label: 'Updating resume', value: 52 },
  { label: 'Writing a cover letter', value: 51 },
  { label: 'Preparing for an interview', value: 49 },
  { label: 'Researching a company', value: 45 },
];

const concernCards = [
  {
    title: 'Bias and Fairness',
    copy: '41% cite biased recommendations as a concern about AI in hiring.',
    icon: Scale,
    tone: 'blue',
  },
  {
    title: 'Data Security',
    copy: 'Handle candidate information through appropriate access, security and vendor-review practices.',
    icon: LockKeyhole,
    tone: 'orange',
  },
  {
    title: 'Lack of Transparency',
    copy: '30% cite lack of transparency as a concern about AI in hiring.',
    icon: FileCheck2,
    tone: 'green',
  },
  {
    title: 'Legal Compliance',
    copy: '45% cite legal compliance as a concern about AI in hiring.',
    icon: ShieldCheck,
    tone: 'red',
  },
];

const roadmap = [
  { title: 'Automate Every Step', copy: 'Apply automation across matching, engagement, interviewing, selection and onboarding workflows.' },
  { title: 'Focus on Skills', copy: 'Use structured interviews and assessments to evaluate skills directly and support inclusive hiring.' },
  { title: 'Demand Ethical Standards', copy: 'Choose vendors that commit to ethical practice, compliance and regular independent review.' },
  { title: 'Be Transparent', copy: 'Clearly explain how AI is used so candidates and employees understand its role in hiring.' },
];

const faqItems = [
  {
    question: 'How can HR teams use AI in hiring?',
    answer: 'AI can support repeatable work across sourcing, screening, communication, assessment and interview preparation, while recruiters stay responsible for decisions.',
  },
  {
    question: 'Where are HR teams applying AI in hiring?',
    answer: 'Common applications include resume screening, candidate communications, recruiting, training and assessment workflows.',
  },
  {
    question: 'Can AI make the final hiring decision?',
    answer: 'No. AI can provide decision support and organise evidence, but people should remain responsible for the final hiring decision.',
  },
  {
    question: 'Why distinguish inferred and validated skills?',
    answer: 'Inferred skills come from indirect signals such as a resume. Validated skills are tested directly through assessments, interviews or conversational AI.',
  },
  {
    question: 'What do candidates expect from AI hiring?',
    answer: 'Candidates benefit from clear communication about where AI supports the process and where people make the final decision.',
  },
  {
    question: 'How does SearchJob use AI in hiring?',
    answer: 'SearchJob implements AI job-description generation, sourcing, resume scoring, skills assessments, AI and live interviews, scorecards and recruiter-controlled shortlisting.',
  },
  {
    question: 'What concerns does the report identify?',
    answer: 'Key considerations include misinformation, job displacement, data security, bias, legal compliance, fairness and transparency.',
  },
  {
    question: 'How do we get started with SearchJob?',
    answer: 'Book a demo to discuss the implemented SearchJob workflow and the hiring steps your team wants to support.',
  },
];

function SectionIntro({ title, children, action, headingId }) {
  return (
    <div className="guide-section-intro">
      <h2 id={headingId}>{title}</h2>
      <div className="guide-intro-copy">{children}</div>
      {action}
    </div>
  );
}

function useGuideScrollMotion() {
  const rootRef = useRef(null);

  useEffect(() => {
    const root = rootRef.current;
    if (!root) return undefined;

    const reduceMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    const reveal = (element) => {
      element.classList.add('is-visible');
    };

    const elements = root.querySelectorAll('[data-scroll-animate]');
    if (reduceMotion || typeof IntersectionObserver === 'undefined') {
      elements.forEach(reveal);
      return undefined;
    }

    root.classList.add('has-scroll-motion');

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          reveal(entry.target);
          observer.unobserve(entry.target);
        });
      },
      { rootMargin: '0px 0px -10% 0px', threshold: 0.16 }
    );

    elements.forEach((element) => observer.observe(element));
    return () => {
      observer.disconnect();
      root.classList.remove('has-scroll-motion');
    };
  }, []);

  return rootRef;
}

function ProductDashboard({ compact = false }) {
  const candidates = [
    { name: 'Candidate X', score: 92, meeting: 'Scheduled', statusClass: 'is-scheduled' },
    { name: 'Candidate Y', score: 88, meeting: 'Awaiting confirmation', displayMeeting: 'Awaiting', statusClass: 'is-awaiting' },
    { name: 'Candidate Z', score: 84, meeting: 'Active', statusClass: 'is-active' },
    { name: 'Candidate W', score: 78, meeting: 'Completed', statusClass: 'is-completed' },
  ];
  const candidateInsights = [
    { label: 'Matching Skills', tone: 'is-matching' },
    { label: 'Missing Skills', tone: 'is-missing' },
    { label: 'Critical Missing', tone: 'is-critical' },
    { label: 'Strengths', tone: 'is-matching' },
    { label: 'Summary', tone: 'is-neutral' },
    { label: 'Transcript', tone: 'is-neutral' },
  ];

  return (
    <div className={`guide-dashboard ${compact ? 'guide-dashboard--compact' : ''}`} aria-label="Illustrative Search Job Meet interview dashboard">
      <aside className="guide-dashboard-side" aria-hidden="true">
        <div className="guide-dashboard-mark"><Sparkles size={14} /></div>
        <span><LayoutDashboard size={13} /></span>
        <span><Briefcase size={13} /></span>
        <span className="guide-side-active"><Calendar size={13} /></span>
        <span><UserCheck size={13} /></span>
      </aside>
      <div className="guide-dashboard-main">
        <div className="guide-dashboard-top">
          <div>
            <span className="guide-ui-kicker">Search Job Meet</span>
            <strong>Interview schedule</strong>
          </div>
          <span className="guide-ui-filter">This week</span>
        </div>
        <div className="guide-dashboard-body">
          <div className="guide-applicant-table">
            <div className="guide-table-head"><span>Candidate</span><span>Score</span><span>Meeting</span></div>
            {candidates.map(({ name, score, meeting, displayMeeting = meeting, statusClass }) => (
              <div className="guide-table-row" key={name}>
                <span><FileText size={13} aria-hidden="true" /> {name}</span>
                <b>{score}%</b>
                <em className={statusClass} title={meeting} aria-label={meeting}>{displayMeeting}</em>
              </div>
            ))}
          </div>
          <div className="guide-match-panel">
            <span>Candidate insights</span>
            <div className="guide-match-list">
              {candidateInsights.map(({ label, tone }) => (
                <div className={`guide-match-legend ${tone}`} key={label}><i /> {label}</div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function FAQColumn({ items, startIndex }) {
  const [openItem, setOpenItem] = useState(null);

  return (
    <div className="guide-faq-column">
      {items.map((item, index) => {
        const id = `guide-faq-${startIndex + index}`;
        const isOpen = openItem === id;
        return (
          <article className={`guide-faq-item ${isOpen ? 'is-open' : ''}`} key={item.question}>
            <h3>
              <button
                type="button"
                aria-expanded={isOpen}
                aria-controls={`${id}-answer`}
                onClick={() => setOpenItem(isOpen ? null : id)}
              >
                <span>{item.question}</span>
                <ChevronDown aria-hidden="true" />
              </button>
            </h3>
            <div id={`${id}-answer`} className="guide-faq-answer" hidden={!isOpen}>
              <p>{item.answer}</p>
            </div>
          </article>
        );
      })}
    </div>
  );
}

export default function AIHiringGuide() {
  const guideMotionRef = useGuideScrollMotion();

  useSEO({
    title: 'AI Hiring Guide 2026 | SearchJob',
    description: 'Practical SearchJob guidance for using AI productively, responsibly and with people at the centre of hiring decisions.',
    keywords: 'AI hiring guide, AI recruitment, AI resume screening, skills-based hiring, responsible AI hiring',
    canonical: 'https://searchjob.ai/resources/ai-hiring-guide',
    ogImage: 'https://searchjob.ai/og-image.png',
    type: 'website',
  });

  return (
    <div ref={guideMotionRef} className="ai-guide min-h-screen bg-background">
      <Header solid />
      <main className="guide-main" id="main-content">
        <section className="guide-hero" aria-labelledby="guide-title">
          <div className="guide-container guide-hero-grid">
            <div className="guide-hero-copy">
              <p className="guide-eyebrow"><span>SearchJob</span> AI Hiring Guide</p>
              <h1 id="guide-title">The Practical Guide to <em>AI-Powered Hiring</em></h1>
              <p className="guide-lede">Practical SearchJob guidance for applying AI to hiring with productivity, trust, clear safeguards and human-led decision support.</p>
              <div className="guide-hero-actions">
                <a className="guide-button guide-button--primary" href="/hirewithus/book-demo">Book a Demo <ArrowRight aria-hidden="true" /></a>
              </div>
              <ul className="guide-value-list" aria-label="Guide highlights">
                <li><ShieldCheck aria-hidden="true" /> Practical hiring guidance</li>
                <li><ClipboardCheck aria-hidden="true" /> Recruiter and candidate perspectives</li>
                <li><Lightbulb aria-hidden="true" /> Skills-based decision support</li>
                <li><UsersRound aria-hidden="true" /> Human responsibility in final decisions</li>
              </ul>
            </div>
            <div className="guide-hero-art" aria-label="AI hiring dashboard illustration">
              <div className="guide-hero-glow" aria-hidden="true" />
              <div className="guide-use-case-card">
                <span>Top AI use cases</span>
                <p><i /> Resume screening</p>
                <p><i /> Candidate communications</p>
                <p><i /> Skills assessments</p>
              </div>
              <ProductDashboard compact />
              <div className="guide-insight-card"><Lightbulb aria-hidden="true" /><span><b>Candidate expectation</b>Transparency matters.</span></div>
            </div>
          </div>
        </section>

        <ScrollReveal>
          <section className="guide-section guide-section--split" id="guide-research" aria-labelledby="adoption-heading">
            <div className="guide-container guide-split-grid guide-split-grid--stats">
              <SectionIntro headingId="adoption-heading" title="AI Productivity in Hiring">
                <p>Use AI to support repeatable hiring work, improve workflow focus and help candidates prepare with clarity.</p>
              </SectionIntro>
              <div className="guide-stat-grid" data-scroll-animate>
                {adoptionStats.map(({ value, label, icon: Icon, tone }) => (
                  <article className={`guide-stat-card guide-stat-card--${tone}`} key={label}>
                    <Icon aria-hidden="true" />
                    <strong>{value}</strong>
                    <p>{label}</p>
                  </article>
                ))}
              </div>
            </div>
          </section>
        </ScrollReveal>

        <ScrollReveal>
          <section className="guide-section guide-section--soft" aria-labelledby="impact-heading">
            <div className="guide-container guide-split-grid guide-split-grid--chart">
              <SectionIntro headingId="impact-heading" title="Where HR Uses AI in Hiring">
                <p>AI can support screening, communications, recruiting, training and assessment workflows across the hiring journey.</p>
              </SectionIntro>
              <div className="guide-impact-layout" data-scroll-animate>
                <div className="guide-bar-chart" role="img" aria-label="HR professionals' 2025 AI hiring use cases: resume screening and training 35%; candidate communications 34%; recruiting 33%; candidate screening and assessments 31%.">
                  {impactItems.map(({ label, value, icon: Icon }) => (
                    <div className="guide-bar-row" key={label}>
                      <span><Icon aria-hidden="true" /> {label}</span>
                      <div className="guide-bar-track"><i className="guide-bar-value" style={{ '--bar-value': `${value}%` }} /></div>
                      <b>{value}%</b>
                    </div>
                  ))}
                </div>
                <aside className="guide-benefit-panel">
                  <h3>Candidate use of AI</h3>
                  <ul>
                    {candidateUseItems.map(({ label, value }) => (
                      <li key={label}><span>{label}</span><strong>{value}%</strong></li>
                    ))}
                  </ul>
                </aside>
              </div>
            </div>
          </section>
        </ScrollReveal>

        <ScrollReveal>
          <section className="guide-section" aria-labelledby="perspectives-heading">
            <div className="guide-container guide-split-grid guide-split-grid--comparison">
              <SectionIntro headingId="perspectives-heading" title="Recruiter and Candidate Perspectives">
                <p>Recruiters and candidates can benefit from AI when its role is clear, skills are evaluated directly and people retain responsibility for final decisions.</p>
              </SectionIntro>
              <div className="guide-perspective-wrap" data-scroll-animate>
                <article className="guide-perspective-card guide-perspective-card--hr">
                  <div className="guide-perspective-icon"><BriefcaseBusiness aria-hidden="true" /></div>
                  <h3>HR Professionals</h3>
                  <ul>
                    <li>Use AI to support productive workflows</li>
                    <li>Review recommendations in context</li>
                    <li>Use evidence to inform evaluation</li>
                    <li>Keep people accountable for decisions</li>
                  </ul>
                </article>
                <span className="guide-vs" aria-hidden="true">VS</span>
                <article className="guide-perspective-card guide-perspective-card--candidate">
                  <div className="guide-perspective-icon"><UsersRound aria-hidden="true" /></div>
                  <h3>Candidates / Job Seekers</h3>
                  <ul>
                    <li>Expect clarity about AI use</li>
                    <li>Value human involvement in final decisions</li>
                    <li>Consider fairness and job security</li>
                    <li>Use AI for application preparation</li>
                  </ul>
                  <p className="guide-perspective-note">Candidates distinguish AI assistance from AI making final decisions.</p>
                </article>
              </div>
            </div>
          </section>
        </ScrollReveal>

        <ScrollReveal>
          <section className="guide-section guide-section--soft" aria-labelledby="trust-heading">
            <div className="guide-container guide-split-grid guide-split-grid--trust">
              <SectionIntro headingId="trust-heading" title="Building Trust in AI Hiring">
                <p>Trust depends on clear communication, direct skills evidence, thoughtful review and human responsibility for final hiring decisions.</p>
              </SectionIntro>
              <div className="guide-trust-layout" data-scroll-animate>
                <div className="guide-trust-charts">
                  <article className="guide-trust-card">
                    <div className="guide-trust-ring" style={{ '--chart': '51%' }}><b>51%</b></div>
                    <h3>High trust in workplace AI systems</h3>
                    <p><span>37% (2024) &rarr; </span>51% (2025)</p>
                  </article>
                  <article className="guide-trust-card">
                    <div className="guide-trust-ring" style={{ '--chart': '53%' }}><b>53%</b></div>
                    <h3>High trust in AI hiring recommendations</h3>
                    <p><span>39% (2024) &rarr; </span>53% (2025)</p>
                  </article>
                </div>
                <aside className="guide-principles">
                  <h3>Report emphasis</h3>
                  <ul>
                    <li>Explain how AI is used in hiring</li>
                    <li>Validate skills with direct evidence</li>
                    <li>Keep people responsible for final decisions</li>
                    <li>Use AI to inform, not replace, judgement</li>
                  </ul>
                </aside>
              </div>
            </div>
          </section>
        </ScrollReveal>

        <ScrollReveal>
          <section className="guide-section" id="risk-heading" aria-labelledby="risk-title">
            <div className="guide-container">
              <div className="guide-section-heading guide-section-heading--risk">
                <p className="guide-eyebrow">Responsible AI</p>
                <h2 id="risk-title" className="guide-risk-title">Key Concerns and Risks in AI Hiring</h2>
                <p>Responsible implementation considers misinformation, job displacement, security, biased recommendations, legal compliance, fairness and transparency.</p>
              </div>
              <div className="guide-concern-grid" data-scroll-animate>
                {concernCards.map(({ title, copy, icon: Icon, tone }) => (
                  <article className={`guide-concern-card guide-concern-card--${tone}`} key={title}>
                    <span><Icon aria-hidden="true" /></span>
                    <h3>{title}</h3>
                    <p>{copy}</p>
                  </article>
                ))}
              </div>
            </div>
          </section>
        </ScrollReveal>

        <ScrollReveal>
          <section className="guide-section guide-section--soft" id="decision-support" aria-labelledby="decision-support-heading">
            <div className="guide-container guide-split-grid guide-split-grid--workflow">
              <SectionIntro headingId="decision-support-heading" title="AI as Decision Support: Skills-Based Hiring">
                <p>Use skills assessments and direct evidence to support informed decisions. Be transparent about AI&apos;s role and keep the human factor at the centre of hiring.</p>
              </SectionIntro>
              <div className="guide-workflow" data-scroll-animate>
                <article className="guide-workflow-card">
                  <span className="guide-workflow-label">Resume / Inferred Skills</span>
                  <FileText aria-hidden="true" />
                  <h3>Inferred skills</h3>
                  <div className="guide-skill-tags"><span>Resume claims</span><span>Past roles</span><span>Keywords</span></div>
                  <p>Inferred skills come from indirect signals such as a resume, rather than a direct test of proficiency.</p>
                </article>
                <span className="guide-workflow-arrow" aria-hidden="true"><ArrowRight /></span>
                <article className="guide-workflow-card guide-workflow-card--validated">
                  <span className="guide-workflow-label">Assessments / Validated Skills</span>
                  <ClipboardCheck aria-hidden="true" />
                  <h3>Validated skills</h3>
                  <ul><li>Assessments</li><li>Structured interviews</li><li>Conversational AI</li></ul>
                  <p>Validated skills are tested directly through the hiring process, creating evidence for recruiter review.</p>
                </article>
                <span className="guide-workflow-arrow" aria-hidden="true"><ArrowRight /></span>
                <article className="guide-workflow-card guide-workflow-card--complete">
                  <span className="guide-workflow-label">Human-Led Decision Support</span>
                  <Bot aria-hidden="true" />
                  <h3>Evidence for review</h3>
                  <div className="guide-evidence-list"><span><Check aria-hidden="true" /> Inferred skills</span><span><Check aria-hidden="true" /> Validated skills</span><span><Check aria-hidden="true" /> Recruiter review</span></div>
                  <p>AI can support a decision with evidence; people remain responsible for the final hiring decision.</p>
                </article>
              </div>
            </div>
          </section>
        </ScrollReveal>

        <ScrollReveal>
          <section className="guide-section" id="implementation-heading" aria-labelledby="implementation-title">
            <div className="guide-container guide-split-grid guide-split-grid--roadmap">
              <SectionIntro headingId="implementation-title" title="Winning AI Hiring Strategy">
                <p>Build a responsible AI hiring strategy around automation, skills, ethical standards and transparent communication.</p>
              </SectionIntro>
              <ol className="guide-roadmap" data-scroll-animate>
                {roadmap.map(({ title, copy }, index) => (
                  <li key={title}>
                    <span>{index + 1}</span>
                    <h3>{title}</h3>
                    <p>{copy}</p>
                  </li>
                ))}
              </ol>
            </div>
          </section>
        </ScrollReveal>

        <ScrollReveal>
          <section className="guide-section guide-section--product" id="searchjob-heading" aria-labelledby="searchjob-title">
            <div className="guide-container guide-product-grid">
              <SectionIntro headingId="searchjob-title" title="SearchJob in Action">
                <p>SearchJob&apos;s implemented workflow includes AI job-description generation, candidate sourcing, resume scoring, skills assessments, AI and live interviews, scorecards and recruiter-controlled shortlisting. It gives recruiters evidence to review; it does not replace a human hiring decision.</p>
              </SectionIntro>
              <div className="guide-product-art" data-scroll-animate>
                <ProductDashboard />
              </div>
            </div>
          </section>
        </ScrollReveal>

        <ScrollReveal>
          <section className="guide-outcomes" aria-labelledby="outcomes-title">
            <div className="guide-container guide-outcomes-grid" data-scroll-animate>
              <div className="guide-outcomes-copy">
                <p className="guide-eyebrow">Better hiring, responsibly</p>
                <h2 id="outcomes-title">From Candidates<br />to Business Outcomes</h2>
                <p>SearchJob is designed to organise hiring evidence and workflows while keeping the hiring decision human-led.</p>
              </div>
              <div className="guide-outcome-cards">
                <article><SearchCheck aria-hidden="true" /><span>More informed<br />evaluations</span></article>
                <article><Gauge aria-hidden="true" /><span>More efficient<br />hiring workflows</span></article>
                <article><Scale aria-hidden="true" /><span>Better supported<br />hiring decisions</span></article>
              </div>
              <div className="guide-outcome-art" aria-label="Abstract hiring analytics illustration">
                <div className="guide-bars-art"><i /><i /><i /><i /></div>
                <LineChart aria-hidden="true" />
                <div className="guide-art-document"><FileCheck2 aria-hidden="true" /><span>Evidence summary</span></div>
              </div>
            </div>
          </section>
        </ScrollReveal>

        <ScrollReveal>
          <section className="guide-section guide-section--faq" aria-labelledby="faq-title">
            <div className="guide-container">
              <div className="guide-faq-heading">
                <div>
                  <p className="guide-eyebrow">Helpful answers</p>
                  <h2 id="faq-title">Frequently Asked Questions</h2>
                </div>
              </div>
              <div className="guide-faq-grid">
                <FAQColumn items={faqItems.slice(0, 4)} startIndex={1} />
                <FAQColumn items={faqItems.slice(4)} startIndex={5} />
              </div>
            </div>
          </section>
        </ScrollReveal>

        <section className="guide-final-cta" aria-labelledby="final-cta-title">
          <div className="guide-container guide-final-cta-inner">
            <div>
              <p className="guide-eyebrow"><span>Ready to begin?</span> SearchJob.ai</p>
              <h2 id="final-cta-title">See how SearchJob can help you hire <span>better with AI.</span></h2>
            </div>
            <div className="guide-final-actions">
              <a className="guide-button guide-button--light" href="/hirewithus/book-demo">Book a Demo <ArrowRight aria-hidden="true" /></a>
              <a className="guide-button guide-button--outline-light" href="/hirewithus/contact">Contact</a>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
